/**
 * 当前会话上下文 + 登录用户。
 *
 * `me` 由 /api/auth/me 填充（M2 已实现）；`currentChat` 由 JSSDK 在 webview
 * 内探测出来。两者都可能为 null——前端控件按需降级（如无 chat_id 时禁用
 * "发送到此聊天"按钮）。
 */

import { create } from "zustand";

import type { CurrentChatInfo } from "@/feishu/jssdk";

export interface Me {
  open_id: string;
  name: string | null;
  avatar_url: string | null;
  is_admin: boolean;
}

interface AppStore {
  me: Me | null;
  authReady: boolean;
  currentChat: CurrentChatInfo | null;
  favoritesVersion: number;
  setMe: (me: Me | null) => void;
  setAuthReady: () => void;
  setCurrentChat: (chat: CurrentChatInfo | null) => void;
  bumpFavorites: () => void;
}

const PENDING_CHAT_KEY = "feishu_fav_pending_chat";

function restorePendingChat(): CurrentChatInfo | null {
  try {
    const raw = sessionStorage.getItem(PENDING_CHAT_KEY);
    if (!raw) return null;
    sessionStorage.removeItem(PENDING_CHAT_KEY);
    const obj = JSON.parse(raw) as CurrentChatInfo;
    if (obj?.chat_id && typeof obj.chat_id === "string") return obj;
  } catch { /* ignore */ }
  return null;
}

export const useAppStore = create<AppStore>((set) => ({
  me: null,
  authReady: false,
  currentChat: restorePendingChat(),
  favoritesVersion: 0,
  setMe: (me) => set({ me }),
  setAuthReady: () => set({ authReady: true }),
  setCurrentChat: (currentChat) => {
    set({ currentChat });
    // 同步持久化，防止 OAuth 重定向导致 store 重置
    try {
      if (currentChat) sessionStorage.setItem(PENDING_CHAT_KEY, JSON.stringify(currentChat));
      else sessionStorage.removeItem(PENDING_CHAT_KEY);
    } catch { /* quota */ }
  },
  bumpFavorites: () => set((s) => ({ favoritesVersion: s.favoritesVersion + 1 })),
}));
